import { SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePagination } from '../../hooks/usePagination';
import { usePermissions } from '../../hooks/usePermissions';
import InsuranceRowDrawer from '../../pages/resource-matrix/plans/InsuranceRowDrawer';
import { getPlansList } from '../../services/plans/listPlans';
import { updateGrid } from '../../services/plans/updateGrid';
import {
  InsurancePlan,
  PlansResponse,
} from '../../types/plans/types';
import {
  AgeRange,
  AgeRangeCatalog,
  coerceRangeForBackend,
  generateRowKey,
  isValidRange,
  rangeToLabel,
  reconstructAgeRange,
} from '../../utils/ageRanges';
import { formatProviderName } from '../../utils/customFieldUtils';
import { getStatusConfig } from '../../utils/getStatusConfig';
import { AgeRangeSelect } from '../age-range-select';

interface ProviderCredential {
  id?: string;
  practiceId: string;
  providerId: string;
  plan_id: string;
  planName: string;
  credentialStatus: string;
  value: string;
  va: boolean;
  hmo: boolean;
  ageRange: AgeRange; // Consolidated age range
  ageRangeLabel?: string; // Track the user's selected label for null/null cases
}

// Row state for managing edits
interface RowState {
  rowKey: string;
  credential: ProviderCredential;
  isDirty: boolean;
}

interface ProviderInsuranceTableProps {
  providerId: string;
  practiceIds: string[];
  title?: string;
  hideProviderColumn?: boolean;
  refreshKey?: number; // Key to trigger refetch when changed
  onSaved?: () => void; // Callback to refresh data after save
}

// Single combined dropdown options as requested by client
const COMBINED_STATUS_OPTIONS = [
  'Schedule Allowed',
  'Pending - Do Not Schedule',
  'Not Participating',
  'Established Patients Only',
  'PCP Match Required',
  'No Status',
];

// Helper function to map combined status to credential status and value
const getCombinedStatusMapping = (combinedStatus: string) => {
  switch (combinedStatus) {
    case 'Schedule Allowed':
      return {
        credentialStatus: 'Credentialed',
        value: 'Schedule Allowed',
      };
    case 'Pending - Do Not Schedule':
      return { credentialStatus: 'Pending', value: 'No Status' };
    case 'Not Participating':
      return {
        credentialStatus: 'Credentialed',
        value: 'Not Participating',
      };
    case 'Established Patients Only':
      return {
        credentialStatus: 'Credentialed',
        value: 'Established Patients',
      };
    case 'PCP Match Required':
      return { credentialStatus: 'Credentialed', value: 'PCP Match' };
    case 'No Status':
      return { credentialStatus: 'Credentialed', value: 'No Status' };
    default:
      return { credentialStatus: 'Credentialed', value: 'No Status' };
  }
};

// Helper function to get combined status from credential status and value
const getCombinedStatusFromFields = (
  credentialStatus: string,
  value: string
) => {
  // Debug logging to see what values we're getting
  if (import.meta.env.DEV) {
    console.log('getCombinedStatusFromFields:', {
      credentialStatus,
      value,
      credentialStatusLower: credentialStatus?.toLowerCase(),
    });
  }

  if (credentialStatus?.toLowerCase() === 'pending') {
    return 'Pending - Do Not Schedule';
  }

  // Handle case-insensitive matching for value
  const valueLower = value?.toLowerCase();

  switch (valueLower) {
    case 'schedule allowed':
      return 'Schedule Allowed';
    case 'not participating':
      return 'Not Participating';
    case 'established patients':
      return 'Established Patients Only';
    case 'pcp match':
      return 'PCP Match Required';
    case 'no status':
      return 'No Status';
    default:
      // Debug unknown values
      if (import.meta.env.DEV) {
        console.warn(
          'Unknown value in getCombinedStatusFromFields:',
          {
            originalValue: value,
            valueLower,
            credentialStatus,
          }
        );
      }
      return 'No Status';
  }
};


const ProviderInsuranceTable: React.FC<
  ProviderInsuranceTableProps
> = ({
  providerId,
  practiceIds,
  title = 'Insurance Plans',
  hideProviderColumn = false,
  refreshKey,
  onSaved,
}) => {
  const toast = useToast();
  const { canEditInsurance, isAdmin } = usePermissions();

  // Search and pagination state
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState<
    'ascending' | 'descending'
  >('ascending');
  // Core state management with stable row keys
  const [rowStates, setRowStates] = useState<Map<string, RowState>>(
    new Map()
  );
  const [dirtyRowKeys, setDirtyRowKeys] = useState<Set<string>>(
    new Set()
  );
  const [pendingSaves, setPendingSaves] = useState<
    Map<string, AgeRange>
  >(new Map());
  const pendingSavesRef = useRef(pendingSaves);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{
    completed: number;
    total: number;
    current: string;
  } | null>(null);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<InsurancePlan | null>(null);

  // Build filters object for API
  const filters = useMemo(
    () => ({
      providerIds: [providerId],
      practiceIds,
    }),
    [providerId, practiceIds]
  );

  // Create stable fetch function for pagination
  const fetchPlans = useCallback(
    (startToken: string | null) =>
      getPlansList(startToken, filters, 1000), // Fetch all records for this provider
    [filters]
  );

  // Use pagination hook
  const {
    items: allPlans,
    loading,
    totalCount,
    refetch,
  } = usePagination<InsurancePlan, PlansResponse>(fetchPlans, 1000);

  // Trigger refetch when refreshKey changes
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      refetch?.();
    }
  }, [refreshKey, refetch]);

  // Convert plans to row states with stable keys
  const baseRowStatesData = useMemo(() => {
    if (!allPlans.length) return new Map<string, RowState>();

    const newRowStates = new Map<string, RowState>();

    allPlans.forEach((row) => {
      row.plans?.forEach((plan) => {
        const rowKey = generateRowKey(
          plan?.id,
          row.provider.id,
          row.practice.id
        );

        // Reconstruct age range with preference for numeric fields
        const ageRange = reconstructAgeRange({
          minimumAge: plan?.minimumAge,
          maximumAge: plan?.maximumAge,
          age: plan?.age,
          metadata: plan?.metadata as
            | {
                minimumAge?: number | null;
                maximumAge?: number | null;
              }
            | undefined,
        });

        const credential: ProviderCredential = {
          id: plan?.id,
          practiceId: row.practice.id,
          providerId: row.provider.id,
          plan_id: plan?.insurance?.id || '',
          planName: plan?.insurance?.friendlyName || 'Unknown Plan',
          credentialStatus: plan?.credentialstatus || 'Pending',
          value: plan?.value || 'No Status',
          va: plan?.insurance?.va ?? false,
          hmo: plan?.insurance?.hmo ?? false,
          ageRange,
        };

        // Debug logging to see what credential data we're creating
        if (import.meta.env.DEV) {
          console.log('Creating credential:', {
            planId: plan?.id,
            planName: plan?.insurance?.friendlyName,
            credentialStatus: plan?.credentialstatus,
            value: plan?.value,
            credential,
          });
        }

        newRowStates.set(rowKey, {
          rowKey,
          credential,
          isDirty: false,
        });
      });
    });

    return newRowStates;
  }, [allPlans]);

  // Update ref when pendingSaves changes
  React.useEffect(() => {
    pendingSavesRef.current = pendingSaves;
  }, [pendingSaves]);

  // Reconcile row states when backend data changes
  React.useEffect(() => {
    setRowStates((prevRowStates) => {
      const newRowStates = new Map<string, RowState>();
      const currentPendingSaves = pendingSavesRef.current;

      // Start with base data from backend
      baseRowStatesData.forEach((baseRowState, rowKey) => {
        const existingState = prevRowStates.get(rowKey);
        const pendingAgeRange = currentPendingSaves.get(rowKey);

        // If we have pending saves for this row, use pending age range
        // Otherwise, use existing dirty state or new base state
        if (pendingAgeRange) {
          newRowStates.set(rowKey, {
            ...baseRowState,
            credential: {
              ...baseRowState.credential,
              ageRange: pendingAgeRange,
            },
            isDirty: true,
          });
        } else if (existingState?.isDirty) {
          // Keep existing dirty state
          newRowStates.set(rowKey, existingState);
        } else {
          // Use fresh backend data
          newRowStates.set(rowKey, baseRowState);
        }
      });

      return newRowStates;
    });
  }, [baseRowStatesData]);

  // Convert row states to array for filtering and pagination
  const allCredentials = useMemo(() => {
    return Array.from(rowStates.values()).map(
      (rowState) => rowState.credential
    );
  }, [rowStates]);

  // Client-side search filtering and sorting
  const filteredCredentials = useMemo(() => {
    let filtered = allCredentials;

    // Apply search filter
    if (searchQuery.trim()) {
      const searchTerm = searchQuery.toLowerCase().trim();
      filtered = allCredentials.filter((credential) => {
        // Find the provider and practice names for this credential
        const providerRow = allPlans.find(
          (p) => p.provider.id === credential.providerId
        );
        const practiceRow = allPlans.find(
          (p) => p.practice.id === credential.practiceId
        );

        const providerName = providerRow
          ? formatProviderName(
              providerRow.provider as {
                firstName?: string;
                lastName?: string;
                middleName?: string;
                suffix?: string;
              }
            )
          : '';
        const practiceName = practiceRow
          ? `${practiceRow.practice.name || ''}, ${
              practiceRow.practice.state || ''
            }`.trim()
          : '';

        return (
          providerName.toLowerCase().includes(searchTerm) ||
          practiceName.toLowerCase().includes(searchTerm) ||
          credential.planName.toLowerCase().includes(searchTerm)
        );
      });
    }

    // Apply sorting by plan name like MarketPracticeList
    return filtered.sort((a, b) => {
      const nameA = (a.planName || '').toUpperCase();
      const nameB = (b.planName || '').toUpperCase();
      return sortOrder === 'ascending'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }, [allCredentials, searchQuery, sortOrder, allPlans]);

  // Calculate pagination
  const totalPagesFiltered = Math.ceil(
    filteredCredentials.length / pageSize
  );
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageCredentials = filteredCredentials.slice(
    startIndex,
    endIndex
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleCombinedStatusChange = useCallback(
    (rowKey: string, combinedStatus: string) => {
      setRowStates((prev) => {
        const newRowStates = new Map(prev);
        const rowState = newRowStates.get(rowKey);
        if (rowState) {
          const { credentialStatus, value } =
            getCombinedStatusMapping(combinedStatus);

          newRowStates.set(rowKey, {
            ...rowState,
            credential: {
              ...rowState.credential,
              credentialStatus,
              value,
            },
            isDirty: true,
          });
        }
        return newRowStates;
      });
      setDirtyRowKeys((prev) => new Set(prev).add(rowKey));
    },
    []
  );

  const handleAgeRangeChange = useCallback(
    (
      rowKey: string,
      newAgeRange: AgeRange,
      selectedLabel?: string
    ) => {
      // Debug logging in dev
      if (import.meta.env.DEV) {
        console.log('Age range change:', {
          rowKey,
          newAgeRange,
          selectedLabel,
        });
      }

      setRowStates((prev) => {
        const newRowStates = new Map(prev);
        const rowState = newRowStates.get(rowKey);
        if (rowState) {
          // Check if this is actually a change
          const currentRange = rowState.credential.ageRange;
          const isChanged =
            currentRange.min !== newAgeRange.min ||
            currentRange.max !== newAgeRange.max;

          if (isChanged) {
            newRowStates.set(rowKey, {
              ...rowState,
              credential: {
                ...rowState.credential,
                ageRange: newAgeRange,
                ageRangeLabel: selectedLabel, // Store the user's selected label
              },
              isDirty: true,
            });

            // Mark as dirty immediately
            setDirtyRowKeys((prevDirty) =>
              new Set(prevDirty).add(rowKey)
            );
          }
        }
        return newRowStates;
      });
    },
    []
  );

  const handleCancelRow = (rowKey: string) => {
    // Reset the row to its original state from base data
    const baseRowState = baseRowStatesData.get(rowKey);
    if (baseRowState) {
      setRowStates((prev) => {
        const newRowStates = new Map(prev);
        newRowStates.set(rowKey, {
          ...baseRowState,
          isDirty: false,
        });
        return newRowStates;
      });
    }

    // Remove from dirty rows
    setDirtyRowKeys((prev) => {
      const copy = new Set(prev);
      copy.delete(rowKey);
      return copy;
    });

    // Show cancel toast
    toast({
      title: 'Edit cancelled',
      description: 'Changes have been reverted',
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });
  };

  // Bulk save functionality with pendingSaves to prevent flicker
  const handleBulkSave = useCallback(async () => {
    if (!canEditInsurance() && !isAdmin()) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to edit plans.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const dirtyRowKeysArray = Array.from(dirtyRowKeys);
    if (dirtyRowKeysArray.length === 0) {
      toast({
        title: 'No Changes to Save',
        description: 'Make some changes before saving.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsBulkSaving(true);
    setBulkProgress({
      completed: 0,
      total: dirtyRowKeysArray.length,
      current: '',
    });

    // Show initial notification
    toast({
      title: 'Saving changes...',
      description: `Starting to save ${
        dirtyRowKeysArray.length
      } plan${dirtyRowKeysArray.length !== 1 ? 's' : ''}.`,
      status: 'info',
      duration: 2000,
      isClosable: true,
    });

    try {
      const results = [];
      const newPendingSaves = new Map(pendingSaves);

      for (let i = 0; i < dirtyRowKeysArray.length; i++) {
        const rowKey = dirtyRowKeysArray[i];
        const rowState = rowStates.get(rowKey);

        if (!rowState) continue;

        const credential = rowState.credential;
        const planName = credential.planName || `Plan ${i + 1}`;
        setBulkProgress({
          completed: i,
          total: dirtyRowKeysArray.length,
          current: planName,
        });

        // Validate age range
        const rangeValid = isValidRange(credential.ageRange);

        if (!rangeValid) {
          results.push({
            status: 'rejected',
            reason: `Invalid age range for ${planName}`,
            rowKey,
          });
          continue;
        }

        try {
          // Add to pending saves to prevent flicker
          newPendingSaves.set(rowKey, credential.ageRange);

          const { minimumAge, maximumAge } = coerceRangeForBackend(
            credential.ageRange
          );

          // Debug logging in dev
          if (import.meta.env.DEV) {
            console.log('Save payload:', {
              rowKey,
              minimumAge,
              maximumAge,
              ageRange: credential.ageRange,
            });
          }

          await updateGrid({
            id: credential.id,
            practiceId: credential.practiceId,
            providerId: credential.providerId,
            plan_id: credential.plan_id,
            credentialStatus: credential.credentialStatus,
            value: credential.value,
            minimumAge,
            maximumAge,
          });

          results.push({
            status: 'fulfilled',
            value: credential,
            rowKey,
          });
        } catch (error) {
          results.push({
            status: 'rejected',
            reason: error,
            rowKey,
          });
        }
      }

      // Update pending saves state
      setPendingSaves(newPendingSaves);

      setBulkProgress({
        completed: dirtyRowKeysArray.length,
        total: dirtyRowKeysArray.length,
        current: 'Complete',
      });

      const failures = results.filter((r) => r.status === 'rejected');
      const successes = results.filter(
        (r) => r.status === 'fulfilled'
      );

      if (failures.length > 0) {
        toast({
          title: 'Some plans failed to save',
          description: `${successes.length} of ${dirtyRowKeysArray.length} plans saved successfully. ${failures.length} failed.`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'All plans saved successfully!',
          description: `${successes.length} plan${
            successes.length !== 1 ? 's' : ''
          } updated.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      // Clear dirty rows for successful saves
      setDirtyRowKeys((prev) => {
        const copy = new Set(prev);
        successes.forEach((result) => {
          if ('rowKey' in result) {
            copy.delete(result.rowKey);
          }
        });
        return copy;
      });

      // Refetch data to reconcile with backend
      await refetch?.();

      // Wait a bit longer to ensure the UI has reconciled with the fresh data
      // before clearing pending saves to prevent UI flicker
      setTimeout(() => {
        setPendingSaves((prev) => {
          const newPending = new Map(prev);
          successes.forEach((result) => {
            if ('rowKey' in result) {
              newPending.delete(result.rowKey);
            }
          });
          return newPending;
        });
      }, 150);

      // Call parent refresh
      onSaved?.();
    } catch (error) {
      toast({
        title: 'Bulk save failed',
        description: 'Failed to save plans. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsBulkSaving(false);
      setBulkProgress(null);
    }
  }, [
    dirtyRowKeys,
    rowStates,
    pendingSaves,
    canEditInsurance,
    isAdmin,
    toast,
    refetch,
    onSaved,
  ]);

  const handleDiscardChanges = useCallback(() => {
    // Reset all dirty rows to their original state from base data
    setRowStates((prev) => {
      const newRowStates = new Map(prev);
      dirtyRowKeys.forEach((rowKey) => {
        const baseRowState = baseRowStatesData.get(rowKey);
        if (baseRowState) {
          newRowStates.set(rowKey, {
            ...baseRowState,
            isDirty: false,
          });
        }
      });
      return newRowStates;
    });

    // Clear all dirty rows
    setDirtyRowKeys(new Set());

    toast({
      title: 'All changes discarded',
      description: 'All unsaved changes have been reverted',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  }, [dirtyRowKeys, baseRowStatesData, toast]);

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedRow(null);
  };

  const handlePlanSaved = () => {
    // First refresh our own data to get the latest values
    setTimeout(async () => {
      await refetch?.();

      // Only call parent refresh after our data is updated
      setTimeout(() => {
        onSaved?.();
      }, 250);
    }, 300);

    toast({
      title: 'Plan updated successfully',
      description: 'The insurance plan has been updated.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const canEdit = canEditInsurance() || isAdmin();

  if (loading && allPlans.length === 0) {
    return (
      <Box>
        <Flex justify="space-between" align="center" mb={4}>
          <Text
            fontSize="1.25rem"
            fontWeight="700"
            color="text.secondary"
          >
            {title}
          </Text>
        </Flex>
        <Box textAlign="center" py={8}>
          <Text>Loading plans...</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      {title && (
        <Flex justify="space-between" align="center" mb={4}>
          <Text
            fontSize="1.25rem"
            fontWeight="700"
            color="text.secondary"
          >
            {title} ({filteredCredentials.length} of{' '}
            {allCredentials.length})
          </Text>
        </Flex>
      )}

      {/* Helper Text */}
      {!searchQuery.trim() && (
        <Text
          fontSize="0.875rem"
          color="gray.600"
          mb="1rem"
          lineHeight="1.4"
        >
          <strong>How to edit:</strong> Change any dropdown values
          below. Modified rows will show "Modified" status. Use the
          "Save All Changes" button to save all your changes at once.
          <br />
          <strong>Note:</strong> The "Status" dropdown combines both
          credential status and patient acceptance into a single,
          clear selection.
        </Text>
      )}

      {/* Search and Pagination Controls */}
      <Flex justify="space-between" align="center" mb="1rem">
        <Flex align="center" gap="1rem">
          <InputGroup maxW="24rem">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder={
                hideProviderColumn
                  ? 'Search plans or practices...'
                  : 'Search providers, practices, and plans...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="provider-insurance-search"
            />
          </InputGroup>

          {/* Sort Select - wider to fit text */}
          <Select
            bg="#F7FAFC"
            height="2.5rem"
            fontSize="0.875rem"
            _hover={{ bg: 'white' }}
            _active={{
              transform: 'scale(0.98)',
            }}
            _focus={{ boxShadow: 'outline' }}
            borderRadius="0.375rem"
            border="1px solid transparent"
            value={sortOrder}
            onChange={(e) =>
              setSortOrder(
                e.target.value as 'ascending' | 'descending'
              )
            }
          >
            <option value="ascending">Plan Name A - Z</option>
            <option value="descending">Plan Name Z - A</option>
          </Select>
        </Flex>

        <Flex align="center" gap="1rem">
          <Text fontSize="0.875rem" color="gray.600">
            Show:
          </Text>
          <Select
            value={pageSize}
            onChange={(e) =>
              handlePageSizeChange(Number(e.target.value))
            }
            width="5rem"
            size="sm"
            data-testid="provider-insurance-pagination"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </Select>
          <Text fontSize="0.875rem" color="gray.600">
            per page
          </Text>
        </Flex>
      </Flex>

      {/* Bulk Actions Bar */}
      {dirtyRowKeys.size > 0 && canEdit && (
        <Box
          mb="1rem"
          p="1.25rem"
          bg="orange.50"
          borderRadius="lg"
          border="2px solid"
          borderColor="orange.300"
          position="relative"
          boxShadow="md"
        >
          {/* Progress Bar */}
          {bulkProgress && (
            <Box width="100%" mb="1rem">
              <HStack justify="space-between" mb="0.5rem">
                <Text
                  fontSize="0.75rem"
                  color="blue.600"
                  fontWeight="500"
                >
                  {bulkProgress.current}
                </Text>
                <Text fontSize="0.75rem" color="blue.600">
                  {bulkProgress.completed} of {bulkProgress.total}
                </Text>
              </HStack>
              <Box
                width="100%"
                height="0.25rem"
                bg="blue.100"
                borderRadius="full"
                overflow="hidden"
              >
                <Box
                  width={`${
                    (bulkProgress.completed / bulkProgress.total) *
                    100
                  }%`}
                  height="100%"
                  bg="blue.500"
                  borderRadius="full"
                  transition="width 0.3s ease"
                />
              </Box>
            </Box>
          )}

          <HStack justify="space-between" align="center">
            <HStack spacing="0.5rem">
              <Text
                fontSize="1rem"
                fontWeight="700"
                color="orange.700"
              >
                {dirtyRowKeys.size} unsaved change
                {dirtyRowKeys.size !== 1 ? 's' : ''}
              </Text>
              <Text
                fontSize="0.875rem"
                color="orange.600"
                fontWeight="500"
              >
                Review your changes and save them all at once
              </Text>
            </HStack>

            <HStack spacing="0.75rem">
              <Button
                size="md"
                variant="outline"
                colorScheme="gray"
                onClick={handleDiscardChanges}
                isDisabled={isBulkSaving}
              >
                Discard Changes
              </Button>
              <Button
                size="md"
                colorScheme="orange"
                onClick={handleBulkSave}
                isLoading={isBulkSaving}
                loadingText="Saving..."
                fontWeight="600"
              >
                Save All Changes ({dirtyRowKeys.size})
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}

      {/* Table */}
      <Box
        bg="white"
        borderRadius="0.5rem"
        overflow="hidden"
        boxShadow="sm"
        maxH="50vh"
        overflowY="auto"
        overflowX="auto"
        width="100%"
        data-testid="provider-insurance-table-container"
      >
        <Table
          variant="simple"
          bg="white"
          borderRadius="0.5rem"
          overflow="hidden"
          size="sm"
          width="100%"
        >
          <Thead bg="gray.50">
            <Tr>
              {!hideProviderColumn && (
                <Th fontWeight="600" color="gray.700" width="12%">
                  Provider
                </Th>
              )}
              <Th
                fontWeight="600"
                color="gray.700"
                width={hideProviderColumn ? '18%' : '15%'}
              >
                Practice
              </Th>
              <Th
                fontWeight="600"
                color="gray.700"
                width={hideProviderColumn ? '20%' : '18%'}
              >
                Plan Name
              </Th>
              <Th fontWeight="600" color="gray.700" width="22%">
                Status
              </Th>
              <Th fontWeight="600" color="gray.700" width="15%">
                Age range
              </Th>
              <Th
                fontWeight="600"
                color="gray.700"
                width={hideProviderColumn ? '21%' : '16%'}
              >
                Status
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentPageCredentials.map((credential) => {
              // Generate stable row key
              const rowKey = generateRowKey(
                credential.id,
                credential.providerId,
                credential.practiceId
              );
              const rowState = rowStates.get(rowKey);
              const isDirty = dirtyRowKeys.has(rowKey);

              // Use row state data if available, otherwise fallback to credential
              const displayCredential =
                rowState?.credential || credential;

              return (
                <Tr
                  key={rowKey}
                  data-testid={isDirty ? 'dirty-row' : undefined}
                  data-rowkey={rowKey}
                  bg={isDirty ? 'blue.50' : 'white'}
                  borderLeft={
                    isDirty ? '3px solid' : '3px solid transparent'
                  }
                  borderLeftColor={
                    isDirty ? 'blue.400' : 'transparent'
                  }
                  transition="all 0.3s ease"
                >
                  {!hideProviderColumn && (
                    <Td>
                      <Text fontWeight="500">
                        {(() => {
                          // Find the correct provider from the original data
                          const providerRow = allPlans.find(
                            (p) =>
                              p.provider.id ===
                              displayCredential.providerId
                          );
                          return providerRow
                            ? formatProviderName(
                                providerRow.provider as {
                                  firstName?: string;
                                  lastName?: string;
                                  middleName?: string;
                                  suffix?: string;
                                }
                              )
                            : 'Unknown Provider';
                        })()}
                      </Text>
                    </Td>
                  )}
                  <Td>
                    <Text fontSize="0.875rem" color="text.secondary">
                      {(() => {
                        // Find the correct practice from the original data
                        const practiceRow = allPlans.find(
                          (p) =>
                            p.practice.id ===
                            displayCredential.practiceId
                        );
                        return practiceRow
                          ? `${practiceRow.practice.name}, ${
                              practiceRow.practice.state || ''
                            }`.trim()
                          : 'Unknown Practice';
                      })()}
                    </Text>
                  </Td>
                  <Td>
                    <Text
                      fontSize="0.875rem"
                      fontWeight="500"
                      color="gray.700"
                      noOfLines={2}
                    >
                      {displayCredential.planName}
                    </Text>
                  </Td>
                  <Td>
                    {(() => {
                      const currentStatus =
                        getCombinedStatusFromFields(
                          displayCredential.credentialStatus,
                          displayCredential.value
                        );
                      const currentStatusConfig = getStatusConfig(
                        displayCredential.value,
                        { va: displayCredential.va, hmo: displayCredential.hmo },
                        undefined,
                        displayCredential.credentialStatus
                      );

                      return (
                        <Menu>
                          <MenuButton
                            as={Button}
                            size="sm"
                            w="220px"
                            minH="2.75rem"
                            h="auto"
                            bg={currentStatusConfig.bgColor}
                            color="white"
                            borderRadius="0.3125rem"
                            fontWeight="600"
                            fontSize="0.75rem"
                            _hover={{
                              filter: 'brightness(1.05)',
                            }}
                            _active={{
                              filter: 'brightness(0.95)',
                            }}
                            rightIcon={
                              <Box
                                as="svg"
                                width="12px"
                                height="12px"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                flexShrink={0}
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </Box>
                            }
                            data-testid="status-dropdown-button"
                          >
                            <Text
                              textAlign="left"
                              width="100%"
                              whiteSpace="normal"
                              lineHeight="1.3"
                            >
                              {currentStatusConfig.label}
                            </Text>
                          </MenuButton>
                          <MenuList
                            w="280px"
                            p={2}
                            borderRadius="0.5rem"
                            boxShadow="lg"
                            border="1px solid"
                            borderColor="gray.200"
                          >
                            {COMBINED_STATUS_OPTIONS.map((option) => {
                              const { credentialStatus, value } =
                                getCombinedStatusMapping(option);
                              const optionConfig = getStatusConfig(
                                value,
                                { va: displayCredential.va, hmo: displayCredential.hmo },
                                undefined,
                                credentialStatus
                              );
                              const isSelected =
                                option === currentStatus;

                              return (
                                <MenuItem
                                  key={option}
                                  onClick={() =>
                                    handleCombinedStatusChange(
                                      rowKey,
                                      option
                                    )
                                  }
                                  bg={
                                    isSelected
                                      ? 'blue.50'
                                      : 'transparent'
                                  }
                                  _hover={{
                                    bg: isSelected
                                      ? 'blue.100'
                                      : 'gray.50',
                                  }}
                                  borderRadius="0.375rem"
                                  mb={1}
                                  p={2}
                                  minH="auto"
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap="0.75rem"
                                    width="100%"
                                  >
                                    <Box
                                      width="1rem"
                                      height="1rem"
                                      borderRadius="0.25rem"
                                      bg={optionConfig.bgColor}
                                      flexShrink={0}
                                    />
                                    <Box flex="1">
                                      <Text
                                        fontSize="0.875rem"
                                        fontWeight={
                                          isSelected ? '600' : '500'
                                        }
                                        color="gray.800"
                                        mb={0.5}
                                      >
                                        {optionConfig.label}
                                      </Text>
                                      <Text
                                        fontSize="0.75rem"
                                        color="gray.600"
                                        lineHeight="1.2"
                                      >
                                        {
                                          optionConfig.helperText.split(
                                            ' | '
                                          )[0]
                                        }
                                      </Text>
                                    </Box>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </MenuList>
                        </Menu>
                      );
                    })()}
                  </Td>
                  <Td>
                    {(() => {
                      // Debug logging to track age range values
                      if (import.meta.env.DEV) {
                        console.log('Age range display debug:', {
                          rowKey,
                          ageRange: displayCredential.ageRange,
                          isDirty,
                          hasPendingSave: pendingSaves.has(rowKey),
                        });
                      }

                      // Use stored label for user selections, fallback to computed label
                      const selectedLabel =
                        displayCredential.ageRangeLabel ||
                        rangeToLabel(displayCredential.ageRange);

                      // Check if we have an exact match in the catalog
                      const matched = AgeRangeCatalog.some(
                        (item) => item.label === selectedLabel
                      );

                      // Controlled select value - always use the computed label
                      let selectValue = selectedLabel;
                      let showCustomOption = false;

                      if (
                        !matched &&
                        !selectedLabel.startsWith('Custom:')
                      ) {
                        // This shouldn't happen with our new logic, but handle gracefully
                        selectValue = 'Select age';
                      } else if (
                        selectedLabel.startsWith('Custom:')
                      ) {
                        // Show custom option for non-catalog values
                        showCustomOption = true;
                        selectValue = selectedLabel; // Show the custom label as disabled option
                      }

                      if (import.meta.env.DEV) {
                        console.log('Age range display result:', {
                          selectedLabel,
                          matched,
                          selectValue,
                          showCustomOption,
                        });
                      }

                      return (
                        <AgeRangeSelect
                          value={displayCredential.ageRange}
                          onChange={(range, label) => {
                            handleAgeRangeChange(
                              rowKey,
                              range,
                              label
                            );
                          }}
                          size="sm"
                          minWidth="140px"
                          maxWidth="180px"
                          showCustomOption={showCustomOption}
                          label={undefined}
                          customLabel={
                            showCustomOption
                              ? `${selectedLabel} (current)`
                              : undefined
                          }
                          data-testid={`age-range-select-${rowKey}`}
                        />
                      );
                    })()}
                  </Td>
                  <Td>
                    <Flex gap="0.5rem" justify="center">
                      {isDirty ? (
                        <Flex align="center" gap="0.5rem">
                          <Box
                            w="0.5rem"
                            h="0.5rem"
                            borderRadius="full"
                            bg="orange.400"
                            title="Unsaved changes"
                          />
                          <Text
                            fontSize="0.75rem"
                            color="orange.600"
                            fontWeight="500"
                          >
                            Modified
                          </Text>
                          <Tooltip
                            label="Undo all changes to this row"
                            placement="top"
                            hasArrow
                          >
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="gray"
                              onClick={() => handleCancelRow(rowKey)}
                              isDisabled={isBulkSaving}
                              fontSize="0.75rem"
                              px="0.5rem"
                              py="0.25rem"
                              minW="auto"
                            >
                              Undo
                            </Button>
                          </Tooltip>
                        </Flex>
                      ) : (
                        <Text fontSize="0.75rem" color="gray.500">
                          Saved
                        </Text>
                      )}
                    </Flex>
                  </Td>
                </Tr>
              );
            })}
            {currentPageCredentials.length === 0 && (
              <Tr>
                <Td
                  colSpan={hideProviderColumn ? 5 : 6}
                  textAlign="center"
                  py={8}
                >
                  <VStack spacing={3}>
                    <Text
                      fontSize="1rem"
                      fontWeight="500"
                      color="gray.600"
                    >
                      {searchQuery.trim()
                        ? 'No insurance plans match your search'
                        : 'No insurance plans found'}
                    </Text>
                    <Text fontSize="0.875rem" color="gray.500">
                      {searchQuery.trim()
                        ? `Try searching for "${searchQuery.trim()}" in plan names or practices`
                        : "This provider doesn't have any insurance plans configured yet"}
                    </Text>
                    {searchQuery.trim() && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSearchQuery('')}
                      >
                        Clear search
                      </Button>
                    )}
                  </VStack>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination Controls */}
      {totalPagesFiltered > 1 && (
        <Flex justify="center" mt={4}>
          <HStack spacing={2}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </Button>
            <Text fontSize="0.875rem" color="gray.600">
              Page {currentPage} of {totalPagesFiltered}
            </Text>
            <Button
              size="sm"
              variant="outline"
              isDisabled={currentPage === totalPagesFiltered}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}

      {/* Results count */}
      <Text
        fontSize="0.875rem"
        color="gray.600"
        textAlign="center"
        mt={2}
      >
        {searchQuery.trim() ? (
          <>
            Showing {filteredCredentials.length} of{' '}
            {allCredentials.length} results
            {filteredCredentials.length !== allCredentials.length &&
              ` (filtered from ${totalCount} total)`}
          </>
        ) : (
          <>
            Showing {(currentPage - 1) * pageSize + 1}–
            {Math.min(
              currentPage * pageSize,
              filteredCredentials.length
            )}{' '}
            of {filteredCredentials.length}
          </>
        )}
      </Text>

      {/* Edit Drawer */}
      {selectedRow && (
        <InsuranceRowDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          planId={selectedRow.plans[0]?.insurance?.id || ''}
          row={selectedRow}
          onSaved={handlePlanSaved}
        />
      )}

    </Box>
  );
};

export default ProviderInsuranceTable;
